import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { FileText } from "lucide-react";
import RowDetailSheet from "@/components/RowDetailSheet";

const FORMS = [
  { key: "form_20", label: "Form 20 (Application for Registration)" },
  { key: "form_21", label: "Form 21 (Sale Certificate from Dealer)" },
  { key: "form_22", label: "Form 22 (Roadworthiness Certificate)" },
  { key: "form_29_30", label: "Form 29/30 (Transfer — only for used)" },
];

export default function RTOTab() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const emptyForm = { booking_id: "", rto_office: "", road_tax_amount: 0, hsrp_required: true,
                       forms_submitted: Object.fromEntries(FORMS.map((f) => [f.key, false])) };
  const [form, setForm] = useState(emptyForm);
  const [editOpen, setEditOpen] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    const [r, b] = await Promise.all([api.get("/dealership/rto"), api.get("/dealership/bookings")]);
    setRows(r.data); setBookings(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.booking_id || !form.rto_office) return toast.error("Booking and RTO office are required");
    try {
      const r = await api.post("/dealership/rto", form);
      toast.success(`RTO record ${r.data.rto_number} created`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (row) => { setEditOpen(row); setEditForm({ ...row }); };
  const runEdit = async () => {
    try {
      await api.patch(`/dealership/rto/${editOpen.id}`, editForm);
      toast.success("Updated");
      setEditOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bookingLabel = (bid) => {
    const b = bookings.find((x) => x.id === bid);
    return b ? `${b.booking_number} · ${b.customer?.first_name || ""}` : bid.slice(0, 8);
  };

  return (
    <div className="space-y-3" data-testid="rto-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-rto-btn">
          <FileText className="w-3.5 h-3.5" /> New RTO Record
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>RTO#</th><th>Booking</th><th>Office</th><th>Temp Reg</th><th>Permanent Reg</th><th className="text-right">Road Tax</th><th>HSRP</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No RTO records yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`rto-row-${r.rto_number}`}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setDetail(r)}>
                <td className="font-mono text-xs">{r.rto_number}</td>
                <td className="text-xs">{bookingLabel(r.booking_id)}</td>
                <td className="text-xs">{r.rto_office}</td>
                <td className="font-mono text-xs">{r.temp_registration_no || "—"}</td>
                <td className="font-mono text-xs">{r.permanent_registration_no || "—"}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(r.road_tax_amount)}{r.road_tax_paid && <span className="text-emerald-600 ml-1">✓</span>}</td>
                <td className="text-[10px] font-mono uppercase">{!r.hsrp_required ? "n/a" : r.hsrp_fitted ? "fitted" : r.hsrp_ordered ? "ordered" : "pending"}</td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(r)} data-testid={`edit-rto-${r.rto_number}`}>Update</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">New RTO Record</SheetTitle>
            <SheetDescription className="sr-only">Registration & road-tax tracking.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div><Label>Booking *</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="rto-booking"><SelectValue placeholder="Pick booking" /></SelectTrigger>
                <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.customer?.first_name || ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>RTO office *</Label><Input value={form.rto_office} onChange={(e) => setForm({ ...form, rto_office: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="e.g. MH-01" data-testid="rto-office" /></div>
            <div><Label>Road tax amount</Label><Input type="number" value={form.road_tax_amount} onChange={(e) => setForm({ ...form, road_tax_amount: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="road-tax" /></div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.hsrp_required} onChange={(e) => setForm({ ...form, hsrp_required: e.target.checked })} data-testid="hsrp-required" />
              HSRP required in this state
            </label>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Forms submitted</div>
              {FORMS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-xs cursor-pointer mb-1.5" data-testid={`form-${f.key}`}>
                  <input type="checkbox" checked={!!form.forms_submitted[f.key]}
                          onChange={(e) => setForm({ ...form, forms_submitted: { ...form.forms_submitted, [f.key]: e.target.checked } })} />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rto-save">Create</Button>
        </SheetContent>
      </Sheet>

      <Sheet open={!!editOpen} onOpenChange={(v) => !v && setEditOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Update RTO Record</SheetTitle>
            <SheetDescription className="sr-only">Fill temp/permanent reg, HSRP status.</SheetDescription>
          </SheetHeader>
          {editOpen && <>
            <div className="text-xs text-muted-foreground">{editOpen.rto_number} · {editOpen.rto_office}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Temp reg no.</Label><Input value={editForm.temp_registration_no || ""} onChange={(e) => setEditForm({ ...editForm, temp_registration_no: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="temp-reg" /></div>
              <div><Label>Temp reg expiry</Label><Input type="date" value={editForm.temp_reg_expiry_date || ""} onChange={(e) => setEditForm({ ...editForm, temp_reg_expiry_date: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label>Permanent reg no.</Label><Input value={editForm.permanent_registration_no || ""} onChange={(e) => setEditForm({ ...editForm, permanent_registration_no: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="perm-reg" /></div>
              <div><Label>Permanent reg date</Label><Input type="date" value={editForm.permanent_reg_date || ""} onChange={(e) => setEditForm({ ...editForm, permanent_reg_date: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!editForm.road_tax_paid} onChange={(e) => setEditForm({ ...editForm, road_tax_paid: e.target.checked })} /> Road tax paid</label>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!editForm.hsrp_ordered} onChange={(e) => setEditForm({ ...editForm, hsrp_ordered: e.target.checked })} /> HSRP ordered</label>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!editForm.hsrp_fitted} onChange={(e) => setEditForm({ ...editForm, hsrp_fitted: e.target.checked })} data-testid="hsrp-fitted" /> HSRP fitted</label>
            <Button onClick={runEdit} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rto-update">Save changes</Button>
          </>}
        </SheetContent>
      </Sheet>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="RTO record" testId="rto-detail" />
    </div>
  );
}
